<template>
    <div class="scorecard-projection">
        <div class="row mb-3 align-items-start">
            <div class="col-lg-9 col-12 mb-3 mb-lg-0">
                <div class="d-flex align-items-center mb-2">
                    <h3 class="mb-0 me-3">{{ data.fytd_chart.title }}</h3>
                </div>
                <p class="font-color-tertiary mb-0">{{ data.description }}</p>
            </div>
            <div class="col-lg-3 col-12 d-flex justify-content-lg-end">
                <a v-if="data.link && canView('insights')" :href="data.link" class="btn btn-secondary ms-lg-3 w-100 btn-responsive-auto text-center">
                    <img src="/img/icons/avid-icons/bar-chart-10.svg" class="icon me-2" alt="chart icon">View Insights
                </a>
                <div class="d-inline-block w-100" v-else-if="data.link && !canView('insights')" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                    <button class="btn btn-secondary ms-lg-3 w-100 btn-responsive-auto" :disabled="true">
                        <img src="/img/icons/avid-icons/bar-chart-10.svg" class="icon me-2" alt="chart icon">View Insights
                    </button>
                </div>
            </div>
        </div>
        <div class="row mb-3">
            <div class="col-lg-4 col-12 border-lg-end border-0 border-lg-1 mb-3 mb-lg-0">
                <h4>{{ data.fytd_chart.subtitle }}</h4>
                <div>
                    <chart-template 
                        :chart="data.fytd_chart.chart" 
                        :id="`fytd_chart${data.id}`" 
                        :key="`fytd_chart${data.id}`" 
                        :if_benchmark_report="true" 
                        :if_on_report_page="false"
                        :show_title="false"
                        :allow_deep_dive="false"
                        :current_size="'large'"
                        :chart_show_data_labels="true"
                        :show_compressed_numbers="show_compressed_numbers"
                    />
                </div>
            </div>
            <div class="col-lg-8 col-12">
                <div class="ms-lg-3">
                    <h4>Projected Impact on Future Fiscal Years</h4>
                    <chart-template 
                        :chart="data.fy_chart.chart" 
                        :id="`fy_chart${data.id}`" 
                        :key="`fy_chart${data.id}`" 
                        :if_benchmark_report="true" 
                        :if_on_report_page="false"
                        :show_title="false"
                        :allow_deep_dive="false"
                        :current_size="'large'"
                        :chart_show_data_labels="true"
                        :show_compressed_numbers="show_compressed_numbers"
                    />
                </div>
            </div>
        </div>
        <div class="row mb-3">
            <div class="col">
                <div class="d-flex summary">
                    <div class="primary-chart-insight-icon me-3 mt-2">
                        <img class="icon" src="/img/icons/avid-icons/flag.svg" alt="flag icon">
                    </div>
                    <div>
                        <div class="d-flex justify-content-between align-items-center mb-2">
                            <h4 class="mb-0">What If...</h4>
                            <a v-if="showResetButton" href="#" @click.prevent="resetMetric" class="text-primary reset-link">
                                <i class="fas fa-undo-alt me-1"></i>Reset
                            </a>
                        </div>
                        <p class="mb-3">The projections above are based upon the most current time period's trends. However, there is still time to make changes to improve results. <strong>Use the slider below to see how changes made today to the {{ data.fytd_chart.title }} will impact both this year and the 3 years' value to your organization.</strong></p>
                        <div class="d-flex">
                            <div class="d-flex justify-content-center align-items-center">
                                <label class="form-label mb-0">{{ Math.round(min*100) +'%' }}</label>
                            </div>
                            <div class="flex-grow-1 mx-2" :class="{ negative: local_value < 0 }">
                                <VueSlider
                                    v-model="local_value"
                                    :tooltip-formatter="val => Math.round(val*100) +'%'"
                                    :min="min"
                                    :max="max"
                                    :interval="0.01"
                                    :dot-attrs="{ 'aria-label': 'Projection adjustment for ' + (data.fytd_chart ? data.fytd_chart.title : 'metric') }"
                                    @dragStart="dragging = true"
                                    @dragEnd="dragEnd"
                                    @change="change"
                                    tooltip="always"
                                    tooltip-placement="bottom"
                                />
                            </div>
                            <div class="d-flex justify-content-center align-items-center">
                                <label class="form-label mb-0">{{ Math.round(max*100) +'%' }}</label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<script>
    import VueSlider from 'vue-3-slider-component'
    import permissionsMixin from '../../mixins/permissions'

    export default {
        name: 'ScorecardProjectionChart',
        components: {
            VueSlider,
        },
        mixins: [permissionsMixin],
        props: {
            data: {
                type: Object,
                required: true
            },
            modelValue: {
                type: Number,
                default: 0
            },
            show_compressed_numbers: {
                type: Boolean,
                default: true
            }
        },
        data() {
            return {
                local_value: null,
                min: -1,
                max: 1,
                dragging: false,
                updateTimeout: null
            };
        },
        watch: {
            modelValue(newValue) {
                // Update local_value when the modelValue prop changes (e.g., from resetAllMetrics)
                if (this.local_value !== newValue) {
                    this.local_value = newValue;
                }
            }
        },
        computed: {
            formattedValue() {
                return Math.round(this.local_value * 100) + '%';
            },
            showResetButton() {
                // Show reset button only if the current value differs from the original value
                return this.local_value !== this.data.percent_change;
            }
        },
        beforeMount() {
            this.local_value = this.modelValue;
            //Get the second year value
            var rate = 0;
            this.data.fy_chart.chart.size = 'large';
            this.data.fytd_chart.chart.size = 'large';
            Object.keys(this.data.fy_chart.chart.data).forEach(key => {
                let starting_pos = 1;
                //If it is a percentage
                if(this.data.fy_chart.chart.data[key][starting_pos] <= 1)
                    rate = this.data.fy_chart.chart.data[key][starting_pos];
            });

            //Make sure it isn't able to grow above 100%
            if(rate > 0) {
                let max_rate = Math.pow(1/rate, 0.333) - 1;
                if(max_rate < this.max){
                    this.max = Math.floor(max_rate*100)/100;
                    if(this.local_value > this.max)
                        this.local_value = this.max;
                }
            }
            else if(this.local_value > 1){
                this.max = Math.ceil(this.local_value);
            }

            if(this.max < this.local_value)
                this.local_value = 0;
        },
        mounted() {
            // Initialize tooltips after component is mounted
            this.$nextTick(() => {
                this.initTooltips();
            });
        },
        updated() {
            // Initialize tooltips on every update
            this.$nextTick(() => {
                this.initTooltips();
            });
        },
        methods: {
            initTooltips() {
                // Initialize tooltips only within this component
                try {
                    const componentEl = this.$el;
                    if (componentEl) {
                        const tooltipTriggerList = [].slice.call(componentEl.querySelectorAll('[data-bs-toggle="tooltip"]'));
                        tooltipTriggerList.forEach(el => {
                            try {
                                if (el._tooltipInstance) {
                                    el._tooltipInstance.dispose();
                                }
                                el._tooltipInstance = new window.bootstrap.Tooltip(el);
                            } catch (e) {
                                // Defensive: ignore errors if element is not valid for tooltip
                            }
                        });
                    }
                } catch (e) {
                    // Ignore errors if component element is not available
                }
            },
            change() {
                // Only update the model value during dragging, not the chart
                if(this.dragging) {
                    this.$emit('update:modelValue', this.local_value);
                }
            },
            dragEnd() {
                // Clear any pending updates
                if (this.updateTimeout) {
                    clearTimeout(this.updateTimeout);
                }
                
                // Emit the final change event after drag ends
                this.updateTimeout = setTimeout(() => {
                    this.$emit('update:modelValue', this.local_value);
                    this.$emit('change', this.local_value);
                    this.dragging = false;
                }, 50); // Small delay to ensure smooth transition
            },
            resetMetric() {
                // Reset this individual metric to its original value
                this.local_value = this.data.percent_change;

                // Emit the change event to update the parent component
                this.$emit('update:modelValue', this.local_value);
                this.$emit('change', this.local_value);

                // Emit a custom event to notify the parent that this metric was reset
                this.$emit('reset-metric');
            }
        },
        beforeUnmount() {
            // Dispose of tooltips only within this component
            try {
                const componentEl = this.$el;
                if (componentEl) {
                    const tooltipTriggerList = [].slice.call(componentEl.querySelectorAll('[data-bs-toggle="tooltip"]'));
                    tooltipTriggerList.forEach(el => {
                        try {
                            if (el._tooltipInstance) {
                                el._tooltipInstance.dispose();
                            }
                        } catch (e) {
                            // Ignore errors if tooltip instance is already disposed
                        }
                    });
                }
            } catch (e) {
                // Ignore errors if component element is not available
            }
        }
    }
</script>
